import { GRIP_PAIN_NOTE, GRIP_EFFORT_NOTE } from './gripValidity';
import { combineAMAValues } from './amaMath';
import { ueToWpi, leToWpi } from './amaTables';
import { ratingSummary } from './ratingSummary';

export type Limb = 'right_ue' | 'left_ue' | 'right_le' | 'left_le';
export interface RatedPart {
  id: string;
  label: string;
  limb: Limb | null;
  extremity: number | null;
  wpi: number | null;
}

const TKA_LE_BY_RESULT: Record<string, number> = { good: 37, fair: 50, poor: 75 };

const lastValue = (text: string, pattern: RegExp): number | null => {
  const matches = Array.from(text.matchAll(pattern));
  return matches.length ? Number(matches[matches.length - 1][1]) : null;
};

/** Read final narrative totals only. Never infer an extremity value from WPI.
 * TKA prints only WPI, so its extremity value comes from the Table 17-33 row for
 * the result it states (the same 37/50/75% LE row THA prints), never from the WPI.
 * Advisories belong to the UI: this module never excludes an overlapping rating.
 */
export function ratedParts(narratives: string[]): RatedPart[] {
  const parts: RatedPart[] = [];
  const occurrences = new Map<string, number>();
  for (const narrative of narratives) {
    const text = narrative.replace(/<[^>]*>/g, '');
    if (text === `Grip Strength:\n${GRIP_PAIN_NOTE}` || text === `Grip Strength:\n${GRIP_EFFORT_NOTE}`) continue;
    const title = narrative.match(/^<b><u>([^<]+?)<\/u><\/b>/)?.[1].replace(/:$/, '')
      ?? text.match(/^([^\n:]+):/)?.[1] ?? 'Unrecognized rating';
    const body = text.slice(title.length).replace(/^[:\s]+/, '');
    if (/^No (?:ratable )?impairment\b/i.test(body)) continue;
    const side = title.match(/^(Right|Left)\b/i)?.[1].toLowerCase();
    let limb: Limb | null = null;
    if (side && /^(Right|Left) (Shoulder|Elbow|Wrist|Hand(?: Impairment)?|Grip Strength)$/i.test(title)) limb = `${side}_ue` as Limb;
    else if (side && /^(Right|Left) (Hip|Knee|Ankle|TKA|THA|Total Hip Arthroplasty)$/i.test(title)) limb = `${side}_le` as Limb;

    const wpi = lastValue(text, /(\d+)% (?:WPI|whole person impairment)\b/gi);
    let extremity: number | null = null;
    if (limb !== null) {
      const upper = limb.endsWith('ue');
      extremity = lastValue(text, upper
        ? /Final Upper Extremity Impairment\s*=\s*(\d+)%/gi
        : /Final Lower Extremity Impairment\s*=\s*(\d+)%/gi);
      if (extremity === null && /Hand(?: Impairment)?$/i.test(title)) {
        extremity = lastValue(text, /Upper extremity impairment:[^\n]*=\s*(\d+)% UEI/gi);
      }
      if (extremity === null && /Grip Strength$/i.test(title)) {
        extremity = lastValue(text, /(\d+)% upper extremity impairment converts to/gi);
      }
      if (extremity === null && /(?:TKA|THA|Total Hip Arthroplasty)$/i.test(title)) {
        extremity = lastValue(text, /(\d+)% lower extremity impairment/gi);
      }
      if (extremity === null && /TKA$/i.test(title)) {
        const result = text.match(/total knee replacement with a (good|fair|poor) result/i)?.[1].toLowerCase();
        if (result) extremity = TKA_LE_BY_RESULT[result];
      }
    }
    const base = title.toLowerCase();
    const occurrence = occurrences.get(base) ?? 0;
    occurrences.set(base, occurrence + 1);
    parts.push({ id: `${base}:${occurrence}`, label: ratingSummary([narrative])[0]?.label ?? title, limb, extremity, wpi });
  }
  return parts;
}

export interface CombinedTotal {
  selected: RatedPart[];
  steps: string[];
  wpi: number | null;
  unavailable?: string;
}
const limbLabels: Record<Limb, string> = {
  right_ue: 'Right upper extremity', left_ue: 'Left upper extremity',
  right_le: 'Right lower extremity', left_le: 'Left lower extremity',
};
export function combinedTotal(parts: RatedPart[], selectedIds = parts.map(p => p.id)): CombinedTotal {
  const selected = parts.filter(p => selectedIds.includes(p.id));
  const steps: string[] = [];
  const reasons = selected.flatMap(p => {
    if (!p.limb) return [`${p.label}: unrecognized rating title`];
    if (p.wpi === null) return [`${p.label}: no final WPI value in the report`];
    if (p.extremity === null) return [`${p.label}: no extremity value in the report; WPI cannot be back-converted`];
    return [];
  });
  if (reasons.length) return { selected, steps, wpi: null, unavailable: reasons.join('; ') };
  const wpis: number[] = [];
  for (const limb of Object.keys(limbLabels) as (keyof typeof limbLabels)[]) {
    const values = selected.filter(p => p.limb === limb).map(p => p.extremity!).sort((a, b) => b - a);
    if (!values.length) continue;
    const extremity = combineAMAValues(values);
    const upper = limb.endsWith('ue');
    const wpi = upper ? ueToWpi(extremity) : leToWpi(extremity);
    wpis.push(wpi);
    steps.push(`${limbLabels[limb]}: ${values.map(v => `${v}%`).join(' and ')} = ${extremity}% ${upper ? 'UE' : 'LE'} → ${wpi}% WPI`);
  }
  const wpi = combineAMAValues(wpis);
  if (wpis.length > 1) steps.push(`Whole person: ${wpis.sort((a, b) => b - a).map(v => `${v}%`).join(' and ')} = ${wpi}% WPI`);
  return { selected, steps, wpi };
}

export function combinedTotalNarrative(total: CombinedTotal): string | null {
  if (total.selected.length < 2) return null;
  return [
    '<b><u>Combined Whole Person Impairment</u></b>',
    `Included: ${total.selected.map(p => p.label).join(', ')}.`,
    total.unavailable ? `Combined WPI unavailable: ${total.unavailable}.` : `Combined WPI: ${total.wpi}%.`,
    ...total.steps,
    'Selected impairments are combined largest first within each extremity, then converted once to whole person using Table 16-3 p439 (upper extremity) or Table 17-3 (lower extremity). Limb WPIs are combined using the Combined Values Chart p604, AMA Guides 5th Edition.',
  ].join('\n\n');
}
